import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { IDashboardData, IFavoriteData, IFavoriteResult } from "../common/interfaces";
import { AnalyticType, ModuleType, ModuleName } from "../common/enums";
import { PermissionResourcesRecruitment } from "../../modules/recruitment/utils/recruitment-permission-resources";
import { SecurityApiService } from "@senior-gestao-pessoas/angular-components";

@Injectable({
	providedIn: 'root'
})
export class AnalyticsService {

	public whenCardAppears = new Subject<any>();
	public permissionsMyTabs = [];
	public permissionsTeamTabs = [];
	public cardsRoutesSubject = new BehaviorSubject<any[]>([]);
	public isUsingNewVacancyManagement: boolean;
	public canCallVacancyManagement: boolean;
	private favorites = new Subject<any>();
	private favoritesDashboards = new Subject<any>();
	private favoritesList: IDashboardData[];

	constructor(private http: HttpClient, private securityApiService: SecurityApiService) {
	}

	public favoriteListQuery(analyticType) {
		this.favoritesList = null;
		const params = {
			"analyticType": analyticType
		};
		return this.http.post('platform/analytics/queries/favoriteListQuery', params)
			.subscribe((payload: IFavoriteResult) => this.setFavorite(payload));
	}

	public setFavorite(payload: IFavoriteResult) {
		this.setFavoriteList(payload.result.favorites);
		this.favorites.next(payload.result);
	}

	public getFavorite(): Subject<any> {
		return this.favorites;
	}

	public saveFavorite(params) {
		return this.http.post("platform/analytics/entities/favorite", params);
	}

	public deleteFavorite(id) {
		return this.http.delete(`platform/analytics/entities/favorite/${id}`);
	}

	public setFavoriteList(favorites: IFavoriteData[]) {
		const dataParams: IDashboardData[] = [];
		favorites.map((val: IFavoriteData) => {
			val.dashboards.map(dash => dataParams.push(dash));
		});
		this.favoritesList = dataParams;
		this.favoritesDashboards.next(dataParams);
	}

	public getFavoritesDashboards(): Observable<any> {
		return this.favoritesDashboards.asObservable();
	}

	public getFavoriteList() {
		return this.favoritesList;
	}

	/**
	 * Metódos para manipular o filtro avançado da plataforma
	 */
	public getFiltersByTicket(ticketFilter: string, service = "analytics", domain = 'hcm') {
		return this.http.post('platform/filter_service/queries/getFiltersByTicket',
			{
				ticket: ticketFilter,
				domain: domain,
				service: service
			});
	}

	public getFiltersOfRecruitment() {
		return this.http.post('platform/filter_service/queries/getFilters',
			{
				serviceContexts: [
					{
						contextId: "vacancyManagementContext",
						domain: "hcm",
						service: "vacancymanagement",
						contextLabel: "Recrutamento"
					}
				],
			});
	}

	public setFilters(newFilters: any[], activeTicket: string) {
		return this.http.post('platform/filter_service/actions/setTicketFilters',
			{filters: newFilters, ticketFilter: activeTicket});
	}

	/**
	 * Utilizar este método via active route!
	 */
	public getUserRolesByWorkstationId(workstationId: string): Observable<any> {
		return this.http.get(`HCM/userrole/workstation/${workstationId}/descendants`);
	}

	/**
	 * Retorna todos os papéis ativos do colaborador no contexto de analytics informado.
	 */
	public getUserRolesActiveBasedOnContext(analyticType: AnalyticType, employeeId: string): Observable<any[]> {
		return this.http.get<any[]>(`HCM/userrole/analytics-context/${analyticType}/${employeeId}`);
	}

	public getHyperlinks(employeeId, userRoleId): Observable<any> {
		return this.http.get(`HCM/hyperlink/all/${employeeId}/${userRoleId}`);
	}

	public getUserRoleActiveBasedOnContext(analyticType: AnalyticType, employeeId: string): Observable<any> {
		return this.http.get(`HCM/userrole/${analyticType}/${employeeId}`);
	}

	checkModule(moduleName: ModuleName): Observable<any> {
		return this.http.post('hcm/analytics/queries/hasModule', { moduleName });
	}

	getCanUseVacancyManagement(): Observable<any> {
		return this.http.get('hcm/recruitment/queries/canUseVacancyManagement');
	}

	checkCanCallVacancyManagement(favoriteList): void {
		favoriteList.forEach((data: any) => {
			if (data.moduleType && (data.moduleType === ModuleType.RECRUITMENT || data.moduleType === ModuleType.CAREER)) {
				this.canCallVacancyManagement = this.securityApiService.isVisibleStatic(PermissionResourcesRecruitment.VIEW_VACANCY);
			}
		});
	}
}
